// Copyright (c) Ping. All rights reserved.

import { useCallback, useEffect, useRef, useState } from 'react';
import { StyleSheet, Text, View, type ViewStyle } from 'react-native';

import {
  BootstrappingScreen,
  type SessionStatus,
  start,
  stop,
  useConnectionStore,
  useSessionStore,
} from '@ping/ui';
import { bootstrapConversationsFromServer } from '@ping/ui/src/sync/bootstrapConversationsFromServer';
import { createWebConversationsPersister } from '@ping/ui/src/sync/conversationsPersister.web';
import { registerImageByteCache } from '@ping/ui/src/sync/imageByteCache';
import { registerMediaUrlResolver } from '@ping/ui/src/sync/mediaUrlResolver';
import {
  registerCurrentUserAccountId,
  registerCurrentUserDisplayName,
  registerSelfDeviceIdsHex,
} from '@ping/ui/src/sync/registerCurrentUser';

import { listSelfDeviceIdsHex } from '@core/network/users';
import { db,webMediaBlobCache  } from '@core/persistence/db';
import { fetchMediaDownloadUrl } from '@features/chat/mediaUpload';
import { recoverRemovedRejoins } from '@features/chat/rejoinRecovery';
import { commitPendingProposalsAndSurfaceStranded } from '@session/pendingProposalCommitter';
import { BootstrapFailure,bootstrapWebSession,getCurrentTransport  } from '@session/sessionBootstrap';

import { isFeatureEnabled } from './core/featureFlags';
import { log } from './core/log/log';
import { installRefreshInterceptor } from './core/network';
import { performRefresh, setAccessToken } from './core/network/interceptor';
import { retrieveIdentity } from './core/persistence/identity';
import {
  hasStoredCredentialRecord,
  jwtExpiryMs,
  retrieveAccessToken,
  retrieveRefreshToken,
} from './core/persistence/tokens';
import { installPushDeliveryListener } from './core/push/installPushListeners';
import { drainPendingPushWake } from './core/push/pendingPushWake';
import { consumePushWakeUrl } from './core/push/pushWakeRoute';
import { installSync, teardownSync } from './core/sync/bootstrap';
import AuthFlowCoordinator from './features/auth/flow/AuthFlowCoordinator';
import { runPostSignupRecoveryBackup } from './features/auth/postSignupRecoveryBackup';
import {
  readRecoveryBackupStatus,
  RecoveryBackupSetupHost,
} from './features/auth/RecoveryBackupSetupHost';
import { RecoveryRestoreHost } from './features/auth/RecoveryRestoreHost';
import { importPendingRecoverySnapshots } from './features/auth/recoveryRestorePending';
import { hydrateAllConversations } from './features/chat/memberProfileHydration';
import {
  consumePendingLinkingHistory,
  isLinkingHistoryPending,
} from './features/history/pendingLinkingHistory';
import { ensureRecoveredPeerAccountAdmittedEverywhere } from './features/history/recoveredPeerAdmit';
import {
  ensureRecoveryDeliveryKeyAndDrain,
  loadDeliveryPrivForLinking,
  publishDeliveryKeyForLinking,
} from './features/history/recoveryPeerHistory';
import { LinkDevicePairingHost } from './features/linking/LinkDevicePairingHost';
import {
  markBootstrappedOnce,
  readHasBootstrappedOnceSync,
} from './session/bootstrapOnceFlag';
import { clearAllLocalSessionState } from './session/clearLocalState';
import { forceCleanReload } from './session/forceCleanReload';
import { hardWipeBrowserAuthStorage } from './session/hardWipeBrowserAuthStorage';
import AppShell from './shell/AppShell';
import {
  drainServerAdmissionLedger,
  resumePendingOutgoingLink,
} from './shell/hosts/SettingsModalHost';

// Wire the host media client into the shared incoming-message hook so it can
// resolve a peer-profile `avatarMediaId` → URL (e.g. the post-link `ping.profile`
// re-send that names + pictures a freshly-linked device's 1:1 rows). Host-static
// — register once at module load.
registerMediaUrlResolver((mediaId) => fetchMediaDownloadUrl(mediaId));

// Back the shared image-byte cache with our persistent IndexedDB blob store so
// remote images (avatars, media) render INSTANTLY from cached bytes on reload
// instead of black-flashing while the presigned S3 URL re-downloads. Keyed by
// the URL's stable content-addressed path.
registerImageByteCache({
  get: (key) => webMediaBlobCache.get(key),
  put: (key, blob) => webMediaBlobCache.put(key, blob, blob.type || 'image/jpeg'),
});

interface SessionState {
  status: SessionStatus;
  bootstrapPromise: Promise<void> | null;
}

/**
 * Race `op` against a deadline and reject with a `TimeoutError` if the
 * deadline wins. Used to bound long-running async work (silent
 * re-auth network refresh, SDK bootstrap) so the bootstrapping
 * spinner can never stay on screen past the bound — the caller's
 * catch path fires the same wipe-and-reset flow it would on any
 * other failure.
 */
async function withTimeout<T>(
  op: Promise<T>,
  deadlineMs: number,
  label: string,
): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      op,
      new Promise<T>((_, reject) => {
        timer = setTimeout(() => {
          reject(new Error(`timeout: ${label} (${deadlineMs}ms)`));
        }, deadlineMs);
      }),
    ]);
  } finally {
    if (timer !== undefined) clearTimeout(timer);
  }
}

type AppPhase =
  | { kind: 'unauthenticated' }
  // QR pairing flow — replaces the auth coordinator while the user
  // is generating a handshake and waiting for ticket redemption.
  | { kind: 'linkingDevice' }
  // Account recovery — OTP + passphrase → decrypt backup → wrap.
  // After success, pops back to `unauthenticated` so the user signs
  // in normally against the restored identity.
  | { kind: 'restoringFromBackup' }
  // `source` lets the screen show context-specific progress copy.
  // 'link' is set when the bootstrap is kicked off by the QR-pairing
  // host so the user sees "Importing your conversations…" instead of
  // the generic "Securing your connection…" cycle. Defaults to `auth`
  // for sign-in / silent-reauth callers that don't set it.
  | { kind: 'bootstrapping'; source?: 'auth' | 'link' | 'recovery' }
  // Returning user on reload: this device has bootstrapped before, so
  // we render the cached AppShell immediately (chat list hydrates from
  // IndexedDB) while the SDK re-initialises silently in the
  // background. No full-screen loader — that only shows on a genuine
  // first signup/login (see `readHasBootstrappedOnceSync`).
  | { kind: 'silentBootstrap' }
  // Post-bootstrap, pre-AppShell: a fresh signup hasn't seen the
  // recovery-key prompt yet. SDK is fully booted so we can pull the
  // identity export, encrypt with the user's chosen passphrase, and
  // POST to /v1/recovery/blobs. Skips immediately for returning
  // sign-ins or for devices that have already been through this
  // flow (marker row in `settings_kv`).
  | { kind: 'creatingRecoveryBackup' }
  | { kind: 'authenticated' };

/**
 * Root component. Drives three states:
 *
 * 1. Unauthenticated → render `<AuthFlowCoordinator />`. When the
 *    coordinator emits `onAuthenticated`, call `bootstrapWebSession`.
 * 2. Bootstrapping → render a chrome-only loading state.
 * 3. Authenticated → render `<AppShell />`.
 *
 * The Zustand session store is the single source of truth for which
 * phase we are in. Closing the GAP-3 / GAP-4 / GAP-5 break is the
 * point of this rewrite — the previous `App.tsx` never drove the
 * store after SMS auth, so `useSession()` stayed `null` and the
 * `ChatRoom` send path was broken.
 */
// Backend account-event `type`s that mean this account's conversation membership
// changed (a peer/sibling started a chat, added us, or a new sibling device
// linked). Any of these triggers an IMMEDIATE conversation reconcile instead of
// waiting for the next poll tick. Kept permissive across the naming the
// ws-fanout push has used / may use, since an unknown type simply falls back to
// the polling ramp (never a regression).
const CONVERSATION_MEMBERSHIP_EVENT_TYPES: ReadonlySet<string> = new Set([
  // The api's membership push (create / add-members / leave) — the primary signal.
  'conversation_membership',
  'convs.refresh',
  'convs_refresh',
  'conversation_added',
  'conversation_created',
  'conversations.changed',
  'membership_changed',
  'device_added',
]);

// Persisted per-device membership version (the `mseq` cursor) for delta sync —
// see docs/architecture/membership-sync.md. Lives in `settings_kv` so it survives
// reloads but is wiped on a full sign-out. `undefined` = never synced (cold start).
const MEMBERSHIP_VERSION_KV_KEY = 'membership.version.v1';

async function readMembershipVersion(): Promise<number | undefined> {
  try {
    const row = await db.settings_kv.get(MEMBERSHIP_VERSION_KV_KEY);
    const v = (row?.value as { v?: unknown } | undefined)?.v;
    return typeof v === 'number' ? v : undefined;
  } catch {
    return undefined;
  }
}

async function writeMembershipVersion(v: number): Promise<void> {
  try {
    await db.settings_kv.put({ key: MEMBERSHIP_VERSION_KV_KEY, value: { v }, updatedAt: Date.now() });
  } catch {
    /* best-effort */
  }
}

// How often the frequent DELTA sync is punctuated by a FULL re-list, which
// refreshes the transport's authoritative membership set (so a server-side
// removal is reflected) and re-anchors the cursor. Deltas carry the steady-state
// load; the full is a rare safety net — NOT the old 2–20s poll.
const MEMBERSHIP_FULL_RESYNC_MS = 5 * 60_000;

// Steady-state cadence of the membership reconcile once the post-connect ramp
// settles. This is a rare HEARTBEAT, not a poll — the real work is done by the
// push (`conversation_membership` account event → immediate delta) and by the
// WS-reconnect kick, exactly how WhatsApp/Slack scale: the persistent connection
// pushes changes; the client never polls "anything new?". A device holding a
// live socket therefore issues one tiny reconcile every 5 min instead of one
// every 20 s — a ~15× cut in steady membership traffic (docs research: 30k→~2k
// req/s at 3M users) with zero correctness loss, because the delta is resumable
// and gap-free by construction and the FULL resync backstops it. Aligned with
// `MEMBERSHIP_FULL_RESYNC_MS` so each heartbeat also retires stale memberships
// and runs reset-detection. See docs/architecture/membership-sync.md
// "Push-authoritative membership (steady poll retired)".
const MEMBERSHIP_HEARTBEAT_MS = MEMBERSHIP_FULL_RESYNC_MS;

export default function App(): JSX.Element {
  const sessionStatus = useSessionStore((state: SessionState) => state.status);
  // Start in `bootstrapping` while we attempt silent re-auth (see
  // useEffect below). If there's no persisted refresh token /
  // identity, the effect immediately flips to `unauthenticated`,
  // which renders <AuthFlowCoordinator />. The brief loading flash
  // beats the previous "always show welcome on reload" behaviour
  // where the user had to sign in again every refresh.
  const [phase, setPhase] = useState<AppPhase>(() => {
    if (sessionStatus.kind === 'authenticated') return { kind: 'authenticated' };
    // Returning user (device has bootstrapped before) → render the
    // cached shell immediately and bootstrap silently. Only a true
    // first run shows the full-screen loader.
    if (readHasBootstrappedOnceSync()) return { kind: 'silentBootstrap' };
    return { kind: 'bootstrapping' };
  });
  // Latched by the auth coordinator when the BE's
  // `recovery_backup_required` flag is true (or the legacy
  // `wasFreshSignup` heuristic fires). Drives the one-time recovery
  // backup prompt after bootstrap completes. Held as a ref (not state)
  // so consuming it doesn't churn the bootstrap-watch effect's deps —
  // the effect would otherwise re-fire on each `setPending(false)` and
  // clobber the freshly-set `creatingRecoveryBackup` phase back to
  // `authenticated` before the user ever sees the recovery modal.
  const pendingRecoveryBackupRef = useRef(false);
  // Mirrors the server's `recovery_backup_required` — when true, the
  // recovery host renders WITHOUT a skip affordance (mandatory).
  const recoveryBackupMandatoryRef = useRef(false);
  // One-shot arrival-wipe guard. Fires the first time the app
  // settles on `unauthenticated` (welcome screen) to clear any
  // orphan IDB / localStorage / sessionStorage state from a prior
  // user. Idempotent under React StrictMode double-invoke.
  const hasWipedOnArrivalRef = useRef(false);
  // Re-entrancy guard so multiple concurrent triggers (refresh
  // interceptor + bootstrap failure + sessionStatus snap-back firing
  // on the same tick) don't queue overlapping wipes against the same
  // IndexedDB. The lock is held for the duration of the wipe + the
  // phase flip and released in `finally`.
  const wipingRef = useRef(false);
  // Counts consecutive transient silent-reauth failures so a logged-in user
  // (credentials present) is RETRIED on the cached shell instead of being
  // bounced to the auth screen. Reset on a successful auth.
  const silentReauthAttemptsRef = useRef(0);
  // True while the silent-reauth retry loop is actively retrying with
  // credentials present. The bootstrap watchdog checks this so it never
  // preempts the (never-give-up) retry loop and bounces a credentialed
  // first-run user to welcome on a slow network — the loop owns recovery.
  const silentReauthActiveRef = useRef(false);
  // Latched true by `onAuthenticated` for any fresh OTP auth — signup OR
  // sign-in (NOT link / recovery, which use separate hosts that never reach
  // `onAuthenticated`). Consumed once by the authenticated-phase effect to
  // ensure a revealable recovery backup exists (the backup task itself no-ops
  // when one already does). Held as a ref so consuming it doesn't churn the
  // bootstrap-watch effect's deps.
  const ensureRecoveryBackupRef = useRef(false);
  // One-shot guard so the background backup is launched at most once per
  // mount even if the authenticated-phase effect re-runs.
  const backgroundBackupLaunchedRef = useRef(false);
  // Latched true by `onRestored` ONLY when a recovery restore just finalized on
  // this device. Consumed once by the authenticated-phase effect to advertise a
  // history-delivery key + start the no-QR recovery peer-history inbox drain.
  // Held as a ref so consuming it doesn't churn that effect's deps.
  const recoveryRestoreRef = useRef(false);

  // Maximum time we'll let the wipe block the welcome screen. If any
  // sub-step (Dexie close, IDB transaction, BE round-trip the helper
  // forgot to fire-and-forget) hangs past this, we forcibly fall
  // through to `unauthenticated` and let the next mount finish
  // cleanup in the background. Picked at 5s because the legitimate
  // path completes in <500ms; anything past that is a hang, not slow.
  const WIPE_DEADLINE_MS = 5000;
  // Hard ceiling on the silent re-auth network round-trip and on the
  // SDK bootstrap (WASM init + WS connect + sync_conversations). On a
  // warm cache the happy path is 1-3s, but a COLD first-visit bootstrap
  // (incognito / new browser) has to download + compile the ~2 MB
  // `ping_wasm` bundle AND generate MLS keys AND make several BE
  // round-trips — that legitimately runs 10-20s on a slow link. The old
  // 12s ceiling fired on those, and `goUnauthenticated` then wiped the
  // tokens out from under the still-running bootstrap, whose fire-and-
  // forget `replenishKeyPackages` then 401'd and logged the user out
  // immediately after a successful sign-up. 30s leaves headroom for a
  // cold start while still catching a genuinely hung worker / dead WS.
  const REAUTH_DEADLINE_MS = 30_000;
  const BOOTSTRAP_DEADLINE_MS = 30_000;

  // Hard reset path: every transition into `unauthenticated` runs
  // through here. The contract is:
  //   * Pre-state — any phase. Caller doesn't need to pre-check.
  //   * Post-state — `unauthenticated`. The wipe is best-effort under
  //     a hard deadline so a stuck Dexie/IDB layer can never trap the
  //     user on the "Preparing your account" spinner. Each sub-step
  //     inside `clearAllLocalSessionState` is already wrapped in its
  //     own try/catch, but the deadline is the belt to the suspenders.
  const goUnauthenticated = useCallback(
    async (reason: string, opts?: { readonly fullWipe?: boolean }): Promise<void> => {
      if (wipingRef.current) {
        log.info({ event: 'app.goUnauthenticated.dedupe', reason });
        return;
      }
      wipingRef.current = true;
      // `fullWipe` is set ONLY for a GENUINE server-side logout: a terminal
      // refresh failure / device-revoked / account-suspended surfaced by the
      // refresh interceptor (which already gates on an unusable access token or
      // the server's `X-Ping-Reauth-Required`). That is the "the server truly
      // logged them out" signal — and the ONLY automatic case where we must
      // destroy the old user's local cache so the next user shares nothing with
      // them. EVERY other automatic path (watchdog, transient bootstrap blip,
      // link/recovery cancel) preserves credentials + cache so a recoverable
      // hiccup never costs the user their session (the P0 invariant).
      const fullWipe = opts?.fullWipe === true;
      log.info({ event: 'app.goUnauthenticated.begin', reason, fullWipe });
      setPhase({ kind: 'bootstrapping' });
      pendingRecoveryBackupRef.current = false;
      try {
        // Race the wipe against the deadline. The deadline-arm just
        // resolves silently so the surrounding `try` catches neither —
        // if the wipe loses, we still fall through to the welcome
        // screen and log it; the next mount's `clearAllLocalSessionState`
        // call (on natural sign-out / silent-reauth-failure paths) can
        // finish the residue.
        await Promise.race([
          (async (): Promise<void> => {
            if (fullWipe) {
              // Match the explicit Sign Out path EXACTLY: forget the device +
              // chats + identity (`mode: 'logout'`), then hard-wipe every
              // app-owned IndexedDB / localStorage / sessionStorage / Cache
              // Storage entry so a signed-out browser holds NO residue of the
              // old user.
              await clearAllLocalSessionState({ reason, mode: 'logout' });
              await hardWipeBrowserAuthStorage();
              return;
            }
            await clearAllLocalSessionState({ reason, preserveCredentials: true });
          })(),
          new Promise<void>((resolve) =>
            setTimeout(() => {
              log.warn({ event: 'app.goUnauthenticated.deadline', reason });
              resolve();
            }, WIPE_DEADLINE_MS),
          ),
        ]);
      } catch (err) {
        // `clearAllLocalSessionState` swallows its own errors per its
        // contract — this catch is for the import / dynamic-error case
        // only. Log but proceed to the welcome screen either way.
        log.warn(
          { event: 'app.goUnauthenticated.wipeFailed', reason },
          (err as Error).message,
        );
      }
      if (fullWipe && typeof window !== 'undefined') {
        // Force a clean-slate reload — same as an explicit Sign Out — so the
        // live tab's in-memory SDK/React state can't keep the old session
        // alive, no service worker survives, and the document is re-fetched
        // fresh rather than restored from the bf/document cache.
        log.info({ event: 'app.goUnauthenticated.fullWipeReload', reason });
        await forceCleanReload(`goUnauthenticated.${reason}`);
        return;
      }
      setPhase({ kind: 'unauthenticated' });
      wipingRef.current = false;
      log.info({ event: 'app.goUnauthenticated.complete', reason });
    },
    [],
  );

  // Belt-and-suspenders backstop on top of the per-operation
  // timeouts. `performRefresh` + `bootstrapWebSession` already bound
  // themselves at REAUTH_DEADLINE_MS + BOOTSTRAP_DEADLINE_MS, and
  // their catch paths route through `goUnauthenticated`. The watchdog
  // only matters for things outside those calls (Dexie `db.open()`
  // deadlock on a migration, IDB read that never resolves) — set
  // generously past the sum of the per-op deadlines so it never
  // preempts a legitimate in-flight bootstrap.
  // Must stay clear of the sum of the per-op deadlines above (a reload
  // can run reauth THEN bootstrap, both in the `bootstrapping` phase) so
  // it never preempts a legitimate cold start — only a true hang.
  const BOOTSTRAP_WATCHDOG_MS = 65_000;

  useEffect(() => {
    if (phase.kind !== 'bootstrapping') return;
    const id = setTimeout(() => {
      // The silent-reauth retry loop owns recovery while it is actively
      // retrying with credentials present: it bounds every step with its own
      // deadlines and never gives up (only a terminal invalid-token via the
      // interceptor signs out). The watchdog must NOT preempt it and bounce a
      // credentialed user to welcome — that is exactly the H2 first-run /
      // slow-network logout. Fire ONLY for a true hang outside that machinery
      // (e.g. a sign-in-driven bootstrap deadlock with no reauth in flight).
      if (silentReauthActiveRef.current) {
        log.warn({ event: 'app.bootstrapping.watchdog.deferred' });
        return;
      }
      log.warn({ event: 'app.bootstrapping.watchdog' });
      void goUnauthenticated('bootstrapping.watchdog');
    }, BOOTSTRAP_WATCHDOG_MS);
    return () => clearTimeout(id);
  }, [phase.kind, goUnauthenticated]);

  // Defensive arrival wipe: fires once the user has actually landed
  // on the welcome screen with no in-flight session (silent reauth
  // failed or never had a session). `goUnauthenticated` already
  // wipes for the logout / bootstrap-failure paths; this hook is the
  // belt-and-suspenders for the first-mount path where the silent
  // reauth saw no persisted refresh token and short-circuited to
  // `unauthenticated` WITHOUT a wipe — leaving any orphan Dexie /
  // IDB rows from a previous user visible to whoever signs in next.
  //
  // Mode selection:
  //   - 'soft' (the just-signed-out path): the user explicitly hit
  //     Sign Out, which set `ping.signoutSoft = 1` in sessionStorage
  //     before reload. Their device-layer keys
  //     (`__device_signing_key` + `__user_identity_key`) must
  //     survive so the SAME user re-signing in this browser matches
  //     the BE's pinned `auth.accounts.user_identity_pubkey`.
  //     Without this, the next verify presents a fresh pubkey and
  //     the BE 403s with `identity_mismatch` — the user is locked
  //     out of their own account on their own browser.
  //   - 'arrival' (fallback): no marker present → either a fresh
  //     visitor or a stale tab where signout state was lost. Default
  //     to the destructive wipe so a different person opening this
  //     browser later can't see the previous user's leftover state.
  useEffect(() => {
    if (phase.kind !== 'unauthenticated') return;
    if (hasWipedOnArrivalRef.current) return;
    hasWipedOnArrivalRef.current = true;
    // Arrival on the welcome screen. This is NEVER a destructive wipe:
    // a reload/refresh failure (even a fast-reload refresh race) must not
    // cost the user their device record, MLS state, chats, or refresh
    // token. We only clear the transient scratch (linking key etc.) via
    // the preserve-everything path. The ONLY thing that wipes the device
    // is an explicit Sign Out (SettingsModalHost), and a different-account
    // sign-in resets SDK storage during its own fresh-identity bootstrap.
    // (Drop the `ping.signoutSoft` marker if present so it doesn't leak.)
    try {
      if (typeof sessionStorage !== 'undefined') {
        sessionStorage.removeItem('ping.signoutSoft');
      }
    } catch {
      /* sessionStorage may be unavailable (private mode, etc.) */
    }
    void clearAllLocalSessionState({
      mode: 'session-failure',
      preserveCredentials: true,
      reason: 'authPage.arrival',
      skipSessionTeardown: true,
    });
  }, [phase.kind]);

  useEffect(() => {
    if (!isFeatureEnabled('FF_NEW_AUTH')) {
      return;
    }
    void installRefreshInterceptor(() => {
      // This callback fires ONLY from `handleTerminalAuthFailure`, i.e. a
      // genuine server logout (dead refresh token with no usable access token,
      // device revoked, or `X-Ping-Reauth-Required`) — exactly the case where
      // the old user's local cache MUST be fully wiped.
      void goUnauthenticated('refreshInterceptor.unauthorized', { fullWipe: true });
    });
  }, [goUnauthenticated]);

  // Silent re-auth on page load. The wrapped refresh token + the
  // identity triple (accountId / deviceId / displayName) are both
  // persisted in IndexedDB during sign-in (EnterCodeScreen). On
  // reload we try to mint a fresh access token off the refresh
  // token and then drive bootstrapWebSession against the recovered
  // identity. Any failure (no persisted material, refresh rejected,
  // identity record missing) tears down the session state and falls
  // through to the auth flow.
  useEffect(() => {
    if (!isFeatureEnabled('FF_NEW_AUTH')) {
      return;
    }
    // Already authenticated (eg. coming from in-tab navigation) — no
    // silent re-auth needed.
    if (sessionStatus.kind === 'authenticated') {
      return;
    }
    let cancelled = false;
    silentReauthActiveRef.current = true;
    const reauth = async (): Promise<void> => {
      try {
        // Dexie `open()` runs migrations on first open. If a prior
        // session crashed mid-migration the open promise can deadlock
        // behind another tab's transaction. The timeout demotes that
        // into a regular failure so the catch path wipes and shows
        // the welcome screen instead of pinning the spinner.
        await withTimeout(db.open(), REAUTH_DEADLINE_MS, 'silentReauth.dbOpen');
        const [refreshToken, identity] = await withTimeout(
          Promise.all([retrieveRefreshToken(), retrieveIdentity()]),
          REAUTH_DEADLINE_MS,
          'silentReauth.readPersisted',
        );
        if (identity === null) {
          // No persisted identity on this device — we genuinely don't know
          // who the user is, so there's no session to restore. Straight to
          // welcome (the clean-slate state already).
          silentReauthActiveRef.current = false;
          if (!cancelled) setPhase({ kind: 'unauthenticated' });
          return;
        }
        // NOTE: a missing REFRESH token is NOT on its own a logout. The BE
        // issues a 365-day access token which we persist; if that's still
        // valid we authenticate off it without ever needing the refresh
        // token. Only when we have NEITHER a valid access token NOR a refresh
        // token is there genuinely no way to re-auth (handled below). This is
        // the fix for "shown the auth screen even though my cache/identity is
        // still here" — the old code bailed the moment `tokens.refresh` was
        // absent, ignoring the still-valid persisted access token.
        // Register "me" from the persisted identity IMMEDIATELY — before
        // bootstrap completes and before the chat list mounts. The message
        // adapter reads this to set `isSelf` (bubble alignment + colour);
        // registering it late (only at the end of bootstrap) is what made
        // self bubbles render left-aligned on reload until identity
        // resolved. Doing it here means alignment is correct on the first
        // paint via the exact-match path (no shape heuristic needed).
        registerCurrentUserAccountId(identity.accountId);
        registerCurrentUserDisplayName(identity.displayName ?? null);
        // Learn this account's device ids so a sibling device's messages render
        // as self on this device. Fire-and-forget; bubbles re-align on resolve.
        void listSelfDeviceIdsHex(identity.accountId).then(registerSelfDeviceIdsHex);
        // FAST PATH — reuse the persisted access token. The BE issues a
        // 365-day access token; if we still have a valid one, we DON'T
        // need to refresh at all. This is the fix for "logged out on
        // reload / after being away": `/v1/auth/refresh` is SINGLE-USE
        // (it rotates + invalidates the old refresh token), so refreshing
        // on every reload — racing the WS reconnect + the first request —
        // burns the token and 401s. Skipping the refresh when the access
        // token is still valid avoids that entirely.
        const storedAccess = await retrieveAccessToken();
        const storedAccessExp =
          storedAccess !== null ? jwtExpiryMs(storedAccess) : null;
        const stillValid =
          storedAccess !== null &&
          storedAccessExp !== null &&
          // 60s skew. An UNPARSEABLE exp (null) is NOT treated as valid:
          // we'd rather mint a fresh token off the refresh token than keep
          // a token we can't reason about (and can't schedule a proactive
          // refresh for).
          storedAccessExp > Date.now() + 60_000;
        if (stillValid && storedAccess !== null && storedAccessExp !== null) {
          // Pass the REMAINING lifetime so proactive refresh is scheduled —
          // otherwise a long-lived reloaded tab keeps a token with unknown
          // expiry and only ever refreshes reactively on a 401.
          const remainingSeconds = Math.max(
            0,
            Math.floor((storedAccessExp - Date.now()) / 1000),
          );
          setAccessToken(storedAccess, remainingSeconds);
        } else if (refreshToken !== null) {
          // No valid access token cached — mint one off the refresh token.
          // Any failure falls to the catch, which retries on the cached shell
          // WITHOUT destroying credentials (a single-use-token race 401 is not
          // a real logout). Only an explicit Sign Out ever wipes.
          await withTimeout(performRefresh(), REAUTH_DEADLINE_MS, 'silentReauth.refresh');
        } else {
          // We couldn't read a usable access OR refresh token this pass.
          // Distinguish "the bytes are still on disk but failed to
          // decrypt/parse" (transient: the AES-GCM wrapper key was momentarily
          // unreadable, or an IDB read hiccuped — `retrieve*Token` swallow that
          // to null) from "there is genuinely no credential record". The former
          // MUST NOT bounce the user to welcome over a storage blip — throw to
          // route into the retry path on the cached shell. Only a true absence
          // means there's nothing to re-auth with.
          const hasCredentialBytes = await hasStoredCredentialRecord();
          if (hasCredentialBytes) {
            log.warn(
              { event: 'app.silentReauth.credentialUnreadable' },
              'credential bytes present but undecryptable this read — retrying (user stays logged in)',
            );
            throw new Error('silentReauth.credentialUnreadable');
          }
          log.warn(
            { event: 'app.silentReauth.noCredential' },
            'identity present but no valid access token or refresh token — showing welcome',
          );
          silentReauthActiveRef.current = false;
          if (!cancelled) setPhase({ kind: 'unauthenticated' });
          return;
        }
        if (cancelled) return;
        await withTimeout(
          bootstrapWebSession({
            tokens: { hasAccessToken: true },
            identity,
          }),
          BOOTSTRAP_DEADLINE_MS,
          'silentReauth.bootstrap',
        );
        // Bootstrap fully succeeded — stamp the device so subsequent
        // reloads skip the loader and render the cached shell.
        void markBootstrappedOnce();
        silentReauthAttemptsRef.current = 0;
        silentReauthActiveRef.current = false;
        if (!cancelled) {
          setPhase({ kind: 'authenticated' });
        }
      } catch {
        // Reached only for TRANSIENT failures (db.open timeout, refresh
        // network blip, bootstrap deadline) — a genuine invalid-token already
        // returned above, and we only got here AFTER reading a non-null
        // refresh token + identity, so the user HAS credentials. A logged-in
        // user must NEVER be bounced to the auth screen on a recoverable blip:
        // RETRY on the cached shell (the phase stays `silentBootstrap` /
        // `bootstrapping`, never `unauthenticated`), and only fall through to
        // the welcome screen once retries are exhausted. Credentials survive
        // throughout, so even then the next reload silently re-auths.
        if (cancelled) return;
        silentReauthAttemptsRef.current += 1;
        const attempt = silentReauthAttemptsRef.current;
        // Capped exponential backoff (1.2s → 2.4s → … → 30s ceiling). Transient
        // failures — 429 rate-limit, network blips, 5xx, deadline timeouts —
        // must NEVER bounce a credentialed user to the welcome screen. A
        // genuinely invalid refresh token is a *terminal* failure that returns a
        // 401 → the refresh interceptor (goUnauthenticated 'refreshInterceptor.
        // unauthorized') handles that, not this catch. So here we ONLY ever
        // retry; we never sign the user out.
        const delay = Math.min(30_000, 1200 * 2 ** Math.min(attempt - 1, 5));
        log.warn(
          { event: 'app.silentReauth.retry', attempt },
          'silent re-auth transient failure; retrying (user stays logged in)',
        );
        if (readHasBootstrappedOnceSync()) {
          // We've rendered the app on this device before — show the CACHED shell
          // and keep retrying the bootstrap in the background. The user keeps
          // working on cached data; sync reconnects when the API recovers. We
          // never give up (the only exit is a real invalid-token via the
          // interceptor, or a manual sign-out).
          setPhase((p) => (p.kind === 'authenticated' ? p : { kind: 'authenticated' }));
          setTimeout(() => {
            if (!cancelled) void reauth();
          }, delay);
          return;
        }
        // First-ever bootstrap on this device: there's no shell to fall back to,
        // so keep retrying on the loader. Persist the credentials regardless, so
        // even a hard give-up re-auths silently on the next reload.
        setTimeout(() => {
          if (!cancelled) void reauth();
        }, delay);
      }
    };
    void reauth();
    return () => {
      cancelled = true;
      silentReauthActiveRef.current = false;
    };
    // Run ONCE on mount. We deliberately do NOT depend on
    // `sessionStatus.kind` here: the @ping/ui `bootstrapSession`
    // transitions the session store `unauthenticated → bootstrapping
    // → authenticated`, and depending on `.kind` would re-fire this
    // effect mid-bootstrap. The second run then races the first —
    // re-running `performRefresh` (which mints a fresh access token
    // bound to the *old* device id) while the first run wipes state
    // and generates a *new* SDK signing key. Result: SDK device-id
    // and auth device-id disagree on every subsequent envelope.
    //
    // Re-running silent reauth on demand (e.g. after the user signs
    // out in another tab) is the job of dedicated handlers, not this
    // initial-mount probe.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    consumePushWakeUrl();
  }, []);

  useEffect(() => installPushDeliveryListener(), []);

  useEffect(() => {
    if (phase.kind !== 'authenticated') {
      return;
    }
    void drainPendingPushWake();
  }, [phase.kind]);

  useEffect(() => {
    if (!isFeatureEnabled('FF_WEB_NEW_CHAT')) {
      return;
    }
    let cancelled = false;
    const init = async (): Promise<void> => {
      await db.open();
      if (cancelled) {
        return;
      }
      installSync(db);
    };
    void init();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!isFeatureEnabled('FF_WEB_NEW_CHAT')) {
      return;
    }
    if (sessionStatus.kind === 'authenticated') {
      // Bootstrap fully succeeded (SDK client is live). Stamp the
      // device once so reloads render the cached shell without the
      // full-screen loader. Covers sign-up / sign-in / link / recovery
      // and the silent-reauth reload path alike. Idempotent.
      if (!readHasBootstrappedOnceSync()) {
        void markBootstrappedOnce();
      }
      const persistence = installSync(db);
      // Backstop: `start` already swallows the benign epoch-mismatch during
      // its bootstrap drain; this `.catch` keeps any genuine failure from
      // surfacing as an unhandled promise rejection in the console.
      void start(sessionStatus.client, persistence).catch((err) => {
        log.error({ event: 'app.syncEngine.start.failed' }, (err as Error).message);
      });
      // Heal any conversation rows synced in a PRIOR session (e.g. via
      // device-linking) that still show a placeholder name / missing avatar
      // because they were never opened. New rows are hydrated per-join via
      // `onConversationUpserted`; this is the backstop for ones already on disk.
      void hydrateAllConversations();
      // [#3c] If a recovery restore just completed, import the backed-up
      // external-conversation snapshots now that the SDK client is live, so
      // group chats + pre-recovery history reappear. Idempotent + best-effort:
      // no-ops unless a restore stashed snapshots this load.
      void importPendingRecoverySnapshots();
      // [#2] If this device just linked, drain the history the existing device
      // shipped (sealed AppEvents for the conversations it admitted us to) and
      // replay it so pre-link messages appear. No-ops unless a linking host
      // stashed (or persisted) the open keys.
      //
      // The single history-transfer inbox is DESTRUCTIVE on read, so exactly one
      // drain may own it per load. While a linking hand-off is still pending,
      // `consumePendingLinkingHistory` owns it and tries BOTH keys per transfer:
      // the QR ephemeral key (the QR device sealed to it) AND our durable
      // delivery key (a NON-QR sibling, admitted via the server ledger when the
      // QR device is offline, seals to it). We therefore do NOT also run the
      // recovery delivery-key drain this load — it would consume + drop the
      // linking bundles (wrong key) and vice-versa. Once linking completes (stash
      // cleared), the recovery drain resumes to catch late sibling-sealed bundles.
      const justRecovered = recoveryRestoreRef.current;
      recoveryRestoreRef.current = false;
      void (async (): Promise<void> => {
        if (await isLinkingHistoryPending()) {
          // Advertise our delivery key so any online sibling can seal past
          // history to us through the ledger (covers the QR device going offline
          // before shipping), then drain the inbox trying both keys.
          await publishDeliveryKeyForLinking();
          const deliveryPriv = await loadDeliveryPrivForLinking();
          await consumePendingLinkingHistory({ deliveryPriv });
          // Conversations admitted during linking have now landed — sweep them
          // so the list shows real peer names/avatars, not "New chat".
          void hydrateAllConversations();
          // A device that ALSO just recovered defers its recovery one-shot to the
          // next load (vanishingly rare); re-latch so it isn't lost.
          if (justRecovered) recoveryRestoreRef.current = true;
        } else {
          // Not linking — pure recovery path: advertise + drain on the
          // post-recovery one-shot, resume an in-progress window otherwise,
          // no-op past the age cap.
          await ensureRecoveryDeliveryKeyAndDrain({ justRecovered });
        }
      })();
      // [#2] Existing-device side: if THIS device started linking another
      // device but the reconcile didn't finish (modal/tab closed before the
      // new device was admitted to every chat), resume it now. No-ops unless a
      // pending outgoing-link record was persisted.
      void resumePendingOutgoingLink();
      // Server-coordinated admission ledger: admit any sibling device that
      // linked and still needs our conversations. Runs on EVERY online device,
      // so admission completes even if the QR-initiating device never returns.
      void drainServerAdmissionLedger();
      // BACKGROUND recovery backup after a fresh OTP auth (signup OR sign-in).
      // The SDK client is live here, so we can generate a mnemonic, build +
      // upload the V3 blob, and stash the phrase locally for later reveal —
      // WITHOUT gating AppShell. Fire-and-forget, one-shot per mount.
      //
      // Why sign-in too: a sign-out wipes the locally-stashed mnemonic, and an
      // OTP sign-in only succeeds by re-onboarding a fresh identity (the server
      // re-pins it when the account has no active devices), which makes the old
      // server blob stale. So on sign-in we mint a fresh phrase + upsert a new
      // blob, restoring a revealable recovery phrase. `runPostSignupRecoveryBackup`
      // is idempotent — it no-ops when this install already has a completed
      // backup AND the mnemonic is still locally retrievable, so a re-verify
      // that kept the phrase never needlessly rotates the key. Link / recovery
      // use separate hosts and never reach this `onAuthenticated`, so they
      // never latch `ensureRecoveryBackupRef`.
      if (ensureRecoveryBackupRef.current && !backgroundBackupLaunchedRef.current) {
        ensureRecoveryBackupRef.current = false;
        backgroundBackupLaunchedRef.current = true;
        void runPostSignupRecoveryBackup();
      }
      // Already in a valid post-auth sub-phase (e.g. the recovery
      // backup modal the user is currently looking at). Don't snap
      // them out into AppShell on the next dep change.
      if (phase.kind === 'creatingRecoveryBackup') {
        return () => {
          void stop().then(() => {
            teardownSync();
          });
        };
      }
      // Server-authoritative recovery gate. When the auth
      // coordinator latched `pendingRecoveryBackupRef`, the BE's
      // `RequireBackedUp` middleware will 403 every endpoint
      // except `/me/session` + `/recovery/blobs` until the upload
      // lands — so we MUST show the recovery host before reaching
      // AppShell. We still consult `readRecoveryBackupStatus` so
      // legacy accounts that completed the prompt on this device
      // before the server-side gate shipped don't see it again
      // (the BE backfill stamps those accounts as backed-up too,
      // so this is belt-and-suspenders).
      if (pendingRecoveryBackupRef.current) {
        const mandatory = recoveryBackupMandatoryRef.current;
        pendingRecoveryBackupRef.current = false;
        let cancelled = false;
        void readRecoveryBackupStatus()
          .then((status) => {
            if (cancelled) return;
            const needsSetup = status === null;
            setPhase(
              needsSetup
                ? { kind: 'creatingRecoveryBackup' }
                : { kind: 'authenticated' },
            );
            // Reset the mandatory ref only after the modal is
            // dismissed so a refresh mid-flow keeps the right copy.
            if (!needsSetup) {
              recoveryBackupMandatoryRef.current = false;
            }
            void mandatory; // kept for clarity; consumed at render time
          })
          .catch(() => {
            if (cancelled) return;
            setPhase({ kind: 'authenticated' });
          });
        return () => {
          cancelled = true;
          void stop().then(() => {
            teardownSync();
          });
        };
      }
      setPhase({ kind: 'authenticated' });
      return () => {
        void stop().then(() => {
          teardownSync();
        });
      };
    }
    // Session store dropped us back to `unauthenticated` while we were
    // mid-authenticated flow — that's a hard reset signal (token
    // refresh rejected mid-session, peer device revoked us, etc.).
    // Route through the wipe helper so we don't leave half-torn-down
    // state behind. The pre-auth sub-phases (linkingDevice,
    // restoringFromBackup, unauthenticated, bootstrapping) are valid
    // "unauthenticated" states from the session store's perspective —
    // ignoring those avoids unmounting the user's flow on the same
    // tick it mounts.
    if (
      sessionStatus.kind === 'unauthenticated' &&
      phase.kind !== 'bootstrapping' &&
      // `silentBootstrap` is a valid pre-client state: the cached shell
      // is up while bootstrap runs in the background and the session
      // store hasn't flipped to `authenticated` yet. Treating it as a
      // reset signal here would wipe the session mid-bootstrap.
      phase.kind !== 'silentBootstrap' &&
      phase.kind !== 'unauthenticated' &&
      phase.kind !== 'linkingDevice' &&
      phase.kind !== 'restoringFromBackup' &&
      phase.kind !== 'creatingRecoveryBackup'
    ) {
      void goUnauthenticated('sessionStore.unauthenticated');
    }
  }, [sessionStatus, phase.kind, goUnauthenticated]);

  // Realtime conversation reconciliation.
  //
  // The WS only pushes conversations this device is SUBSCRIBED to (i.e. has a
  // cursor for). A conversation created on a SIBLING or PEER device — which we
  // become a member of server-side but have no cursor for — is never subscribed,
  // so its Welcome is never delivered and it never appears. (`syncConversations`
  // can't help either: it only iterates conversations the SDK already joined.)
  //
  // Poll `GET /v1/conversations` for the authoritative membership list, then ask
  // the transport to subscribe any conversation we don't yet hold a cursor for —
  // which seeds a from-beginning cursor and re-sends the resume frame, so the BE
  // replays that conversation's Welcome and the SDK performs a real MLS join.
  // This is what makes a chat started on another device show up here in realtime.
  useEffect(() => {
    if (!isFeatureEnabled('FF_WEB_NEW_CHAT')) return;
    if (sessionStatus.kind !== 'authenticated') return;
    let cancelled = false;
    let lastFullAt = 0;
    const persister = createWebConversationsPersister();
    const reconcile = async (): Promise<void> => {
      try {
        // VERSIONED DELTA SYNC (docs/architecture/membership-sync.md). Steady state
        // is an O(changes) delta keyed on the stored membership version — NOT a
        // full re-list. A full walk runs only on cold start, on reset, and on a
        // rare safety cadence. This is what makes the list accurate at any scale
        // without the heavy repeated `GET /v1/conversations` the poll used to do.
        const storedVersion = await readMembershipVersion();
        const wantFull =
          storedVersion === undefined || Date.now() - lastFullAt >= MEMBERSHIP_FULL_RESYNC_MS;
        let result = await bootstrapConversationsFromServer(
          persister,
          wantFull ? {} : { since: storedVersion },
        );
        if (cancelled) return;
        // Fall back to a FULL walk when a delta can't be trusted:
        //  • resetDetected — the backend's version went BELOW ours (rebuild/reset)
        //    → converge on server truth (the full set then retires stale convs).
        //  • currentVersion undefined — the backend doesn't support delta sync
        //    (older deploy) → stay on full lists (graceful degradation).
        if (result.isDelta && (result.resetDetected || result.currentVersion === undefined)) {
          log.warn({
            event: 'app.convReconcile.fullResync',
            reason: result.resetDetected ? 'reset' : 'deltaUnsupported',
            storedVersion,
            currentVersion: result.currentVersion,
          });
          result = await bootstrapConversationsFromServer(persister, {});
          if (cancelled) return;
        }
        const isFull = !result.isDelta;
        const { serverCount, inserted, conversationIdHexes } = result;
        const transport = getCurrentTransport();
        // REVIVE: the server just confirmed these as LIVE memberships. Clear any
        // stale orphan verdict from the transport (a transient 404 earlier this
        // session) and un-hide the rows it soft-hid — but ONLY those it actually
        // orphaned (revive returns wasDead), so we never resurrect a chat the
        // USER intentionally deleted (that never goes through orphaning).
        if (transport !== null) {
          // FULL: feed the AUTHORITATIVE membership set (so a conversation the
          // server no longer lists is retired — the backend-reset self-heal).
          // DELTA: only MERGE the newly-joined ids (we don't have the full set),
          // so a just-joined conv isn't wrongly retired before the next full pass.
          if (isFull) {
            transport.setServerConversations(conversationIdHexes);
            lastFullAt = Date.now();
          } else {
            transport.addServerConversations(conversationIdHexes);
          }
          for (const idHex of conversationIdHexes) {
            if (transport.reviveConversation(idHex)) {
              try {
                await db.conversations.update(idHex.toLowerCase(), { deletedAt: 0 });
              } catch {
                /* best-effort un-hide */
              }
            }
          }
        } else if (isFull) {
          lastFullAt = Date.now();
        }
        // RE-INVITE AFTER REMOVAL: for any server-confirmed membership whose MLS
        // group the SDK no longer holds (we were removed, the SDK dropped it),
        // reset the cursor so the following `subscribeToConversations` replays
        // the full log from zero and the SDK settles on a clean re-join. Runs
        // BEFORE subscribe so the reset cursors are re-requested in the same pass.
        // (In delta mode `conversationIdHexes` is just the new joins — a re-add is
        // itself a new membership row, so it appears here.)
        if (transport !== null && sessionStatus.kind === 'authenticated') {
          await recoverRemovedRejoins({
            client: sessionStatus.client,
            transport,
            serverMemberIdHexes: conversationIdHexes,
          });
        }
        const added =
          transport !== null && conversationIdHexes.length > 0
            ? await transport.subscribeToConversations(conversationIdHexes)
            : 0;
        // Persist the new cursor for the next delta — but only when it's SAFE:
        // a delta always exhausts its pages; a full is only trustworthy if it
        // walked to the end (a truncated walk hasn't seen every conversation).
        if (result.currentVersion !== undefined && result.complete) {
          await writeMembershipVersion(result.currentVersion);
        }
        // serverCount = conversations returned this call (full: all; delta: new);
        // inserted = new Dexie rows; subscribed = convs we seeded a cursor for.
        log.info({
          event: 'app.convReconcile.done',
          mode: isFull ? 'full' : 'delta',
          serverCount,
          inserted,
          subscribed: added,
          version: result.currentVersion,
          hasTransport: transport !== null,
        });
        if (!cancelled && (added > 0 || inserted > 0)) {
          // Newly-discovered/joined conversations need roster names/avatars.
          void hydrateAllConversations();
        }
        // C1/S3: commit any peer "leave" proposals (evict the leaver) and
        // surface stranded conversations. Best-effort, after the membership
        // reconcile so freshly-joined convs are present.
        if (!cancelled && sessionStatus.kind === 'authenticated') {
          void commitPendingProposalsAndSurfaceStranded(sessionStatus.client);
        }
      } catch (err) {
        // 401 during the post-auth device-activation race, transient network,
        // etc. — an earlier retry + the steady tick recover.
        log.warn({ event: 'app.convReconcile.failed', message: (err as Error).message });
      }
    };
    // A brand-new conversation a peer just started is discovered + subscribed by
    // this reconcile. The PRIMARY trigger is now push-authoritative — the backend
    // fans a `conversation_membership` account event to us the instant our
    // membership changes (see `onMembershipEvent` below), and a WS reconnect kicks
    // it too — so the steady loop is a rare HEARTBEAT (`MEMBERSHIP_HEARTBEAT_MS`),
    // not a poll. Ramp fast right after (re)connect — 2s → 4s → 8s → settle at the
    // heartbeat — so a peer's first message lands in ≤~2s of a fresh connect, then
    // self-schedule (never overlapping, unlike setInterval) so a slow reconcile
    // can't stack. This is the WhatsApp/Slack model: the persistent connection
    // pushes changes, the client never polls "anything new?".
    const RAMP_MS = [2_000, 4_000, 8_000];
    const STEADY_MS = MEMBERSHIP_HEARTBEAT_MS;
    let step = 0;
    let handle: ReturnType<typeof setTimeout> | undefined;
    const scheduleNext = (): void => {
      if (cancelled) return;
      const delay = step < RAMP_MS.length ? RAMP_MS[step] : STEADY_MS;
      step += 1;
      handle = setTimeout(() => {
        void reconcile().finally(scheduleNext);
      }, delay);
    };
    // Kick an immediate reconcile + restart the fast ramp. Shared by the `online`
    // event and the backend membership push below.
    const kickReconcileNow = (): void => {
      step = 0;
      if (handle !== undefined) clearTimeout(handle);
      void reconcile().finally(scheduleNext);
    };
    // Kick when the tab comes back online — that's exactly when a peer "returns"
    // and must pull down any conversations started while they were away.
    const onOnline = (): void => kickReconcileNow();
    // INSTANT DELIVERY: ws-fanout pushes a per-account event when this account's
    // conversation membership changes (a peer/sibling started a chat or added us).
    // Reconcile RIGHT NOW on it instead of waiting for the next poll tick — this
    // is what closes the "new chat doesn't appear until reload / up to ~20s" gap.
    // Best-effort: if the push never arrives (older ws-fanout), the escalating
    // ramp still discovers the chat, so this is a latency win, never the sole
    // delivery mechanism.
    const onMembershipEvent = (event: Record<string, unknown>): void => {
      const t = typeof event['type'] === 'string' ? (event['type'] as string) : '';
      // IMMEDIATE peer re-admit: a peer just re-onboarded (fresh MLS leaf after a
      // full-wipe sign-out / recovery) and advertised its delivery key; the backend
      // fanned this out to us because we share a conversation. Re-admit it to every
      // shared chat + ship its missed history NOW, so the returning user resumes all
      // conversations without waiting for us to reopen each one.
      if (t === 'recovery_readmit') {
        const acct = typeof event['account_id'] === 'string' ? (event['account_id'] as string) : '';
        if (acct.length > 0) {
          log.info({ event: 'app.recoveryReadmit.pushKick' });
          void ensureRecoveredPeerAccountAdmittedEverywhere(acct);
        }
        return;
      }
      if (!CONVERSATION_MEMBERSHIP_EVENT_TYPES.has(t)) return;
      log.info({ event: 'app.convReconcile.pushKick', type: t });
      kickReconcileNow();
    };
    const acctEventSub = getCurrentTransport()?.onAccountEvent?.(onMembershipEvent) ?? null;
    // WS RECONNECT KICK: with the steady loop demoted to a 5-min heartbeat, a
    // socket that drops and reconnects (a laptop waking, a flaky mobile link) must
    // re-run the delta immediately — otherwise a membership change whose
    // fire-and-forget push was missed during the outage would wait up to a full
    // heartbeat. The connection store transitions to 'connected' on every
    // (re)connect (fed by the socket layer, deduped so identical transitions don't
    // fire), so this fires exactly once per reconnect. Together, push + reconnect
    // do the real work; the heartbeat is the rare safety net. (`online` covers the
    // OS-level network-restored case; this covers a socket-only reconnect where no
    // `online` event fires.)
    const unsubConn = useConnectionStore.subscribe((s, prev) => {
      if (s.state === 'connected' && prev.state !== 'connected') {
        log.info({ event: 'app.convReconcile.reconnectKick', prev: prev.state });
        kickReconcileNow();
      }
    });
    // First pass immediately, then the escalating ramp.
    void reconcile().finally(scheduleNext);
    if (typeof window !== 'undefined') window.addEventListener('online', onOnline);
    return () => {
      cancelled = true;
      if (handle !== undefined) clearTimeout(handle);
      if (typeof window !== 'undefined') window.removeEventListener('online', onOnline);
      try {
        unsubConn();
      } catch {
        /* best-effort */
      }
      try {
        acctEventSub?.unsubscribe();
      } catch {
        /* best-effort */
      }
    };
  }, [sessionStatus]);

  if (!isFeatureEnabled('FF_NEW_AUTH')) {
    return (
      <View style={styles.fallback}>
        <Text>Ping</Text>
      </View>
    );
  }

  if (phase.kind === 'authenticated' && sessionStatus.kind === 'authenticated') {
    return <AppShell />;
  }

  // Returning-user reload: render the cached shell while the SDK
  // re-initialises in the background. The chat list hydrates from
  // IndexedDB (the conversations adapter's Dexie liveQuery doesn't need
  // the SDK client); the incoming-message/conversation hooks no-op
  // until the client attaches. If the background bootstrap fails, the
  // reauth effect's catch routes through `goUnauthenticated` → welcome.
  if (phase.kind === 'silentBootstrap') {
    return <AppShell />;
  }

  if (phase.kind === 'bootstrapping') {
    // Tailor the copy by source. `link` and `recovery` deliver
    // their own distinct UX:
    //   - link: QR-paired a brand-new device — the user expects
    //     to see CHAT transfer + encryption setup happen.
    //   - recovery: restored from backup on a (potentially new)
    //     device — the user expects to see HISTORICAL state being
    //     pulled in from their backup blob.
    //   - auth (default): fresh signup or returning sign-in —
    //     generic "creating account" cycle from the screen.
    if (phase.source === 'link') {
      return (
        <BootstrappingScreen
          title="Setting up this device"
          steps={[
            'Verifying the link…',
            'Securing your connection…',
            'Importing your conversations…',
            'Syncing encryption keys…',
            'Almost ready…',
          ]}
          // Slightly slower step cadence than the default so the
          // user can read each copy line — link/recovery side
          // bootstrap runs ~6-10 s end-to-end on a fresh device.
          stepDurationMs={1800}
        />
      );
    }
    if (phase.source === 'recovery') {
      return (
        <BootstrappingScreen
          title="Restoring your account"
          steps={[
            'Decrypting your backup…',
            'Restoring your identity…',
            'Loading your conversations…',
            'Syncing encryption keys…',
            'Almost ready…',
          ]}
          stepDurationMs={1800}
        />
      );
    }
    return <BootstrappingScreen />;
  }

  if (phase.kind === 'linkingDevice') {
    return (
      <LinkDevicePairingHost
        // Cancel means "I'm bailing out of pairing entirely" — wipe
        // any partial linking state the host may have written
        // (sessionStorage linking-priv, half-imported SDK rows)
        // before showing the welcome screen.
        onCancel={() => {
          void goUnauthenticated('linkDevice.cancel');
        }}
        onPaired={(user) => {
          // The host already POSTed `/v1/auth/link-verify` with the
          // ticket's pre-signed binding and got back tokens — those
          // are already in module memory (access) + IDB (refresh) +
          // Dexie (identity row). Route straight through the same
          // bootstrap path a normal sign-in would: no pop-back-to-
          // welcome, no second context switch.
          if (sessionStatus.kind === 'authenticated') {
            log.info({ event: 'app.linkDevice.alreadyAuthenticated' });
            return;
          }
          // Linking inherits the account's backup state from
          // link-verify's `recovery_backup_required` field — for
          // existing accounts (which is the only flow that ever
          // reaches linking) this is always false, so we skip the
          // post-bootstrap backup gate.
          pendingRecoveryBackupRef.current = false;
          recoveryBackupMandatoryRef.current = false;
          setPhase({ kind: 'bootstrapping', source: 'link' });
          withTimeout(
            bootstrapWebSession({
              tokens: user.tokens,
              identity: user.identity,
            }),
            BOOTSTRAP_DEADLINE_MS,
            'onPaired.bootstrap',
          ).catch(async (error: unknown) => {
            if (error instanceof BootstrapFailure) {
              log.warn(
                { event: 'app.linkDevice.bootstrapFailed', reason: error.reason },
                error.message,
              );
            } else {
              log.warn(
                { event: 'app.linkDevice.bootstrapFailed' },
                (error as Error).message,
              );
            }
            await goUnauthenticated('linkDevice.bootstrapFailed');
          });
        }}
      />
    );
  }

  if (phase.kind === 'restoringFromBackup') {
    return (
      <RecoveryRestoreHost
        onCancel={() => {
          void goUnauthenticated('restoreFromBackup.cancel');
        }}
        onRestored={(user) => {
          // Recovery now hands us a fully-verified AuthenticatedUser
          // (host drove the second OTP + /v1/auth/verify itself),
          // so we route straight through the same bootstrap path
          // a normal sign-in would. No pop-back-to-welcome, no
          // second context switch — the user lands in AppShell.
          if (sessionStatus.kind === 'authenticated') {
            log.info({ event: 'app.recovery.alreadyAuthenticated' });
            return;
          }
          // Recovery doesn't trigger the post-bootstrap backup gate —
          // the user already HAS a backup (that's how they got here).
          pendingRecoveryBackupRef.current = false;
          recoveryBackupMandatoryRef.current = false;
          // Latch the no-QR recovery peer-history one-shot: once the SDK client
          // is live (authenticated-phase effect) this device advertises a
          // history-delivery key + drains the sealed-history inbox peers post.
          recoveryRestoreRef.current = true;
          setPhase({ kind: 'bootstrapping', source: 'recovery' });
          withTimeout(
            bootstrapWebSession({
              tokens: user.tokens,
              identity: user.identity,
            }),
            BOOTSTRAP_DEADLINE_MS,
            'onRestored.bootstrap',
          ).catch(async (error: unknown) => {
            if (error instanceof BootstrapFailure) {
              log.warn(
                { event: 'app.recovery.bootstrap.failed', reason: error.reason },
                'recovery bootstrap rejected',
              );
            } else {
              log.error({ event: 'app.recovery.bootstrap.failed' }, 'recovery bootstrap rejected');
            }
            await goUnauthenticated('recovery.bootstrap.failed');
          });
        }}
      />
    );
  }

  if (phase.kind === 'creatingRecoveryBackup') {
    return (
      <RecoveryBackupSetupHost
        mandatory={recoveryBackupMandatoryRef.current}
        onDone={() => {
          recoveryBackupMandatoryRef.current = false;
          setPhase({ kind: 'authenticated' });
        }}
      />
    );
  }

  return (
    <AuthFlowCoordinator
      onLinkExistingDevice={() => {
        log.info({ event: 'auth.welcome.link_device' });
        setPhase({ kind: 'linkingDevice' });
      }}
      onRestoreFromBackup={() => {
        log.info({ event: 'auth.welcome.restore_from_backup' });
        setPhase({ kind: 'restoringFromBackup' });
      }}
      onAuthenticated={(user, { wasFreshSignup, recoveryBackupRequired }) => {
        // Defensive: a stale handler from a previous mount could re-fire
        // this callback after the user is already authenticated (e.g.
        // double-click on the final auth step, or React StrictMode's
        // intentional double-invoke in dev). Don't re-bootstrap; the
        // dedupe inside `bootstrapWebSession` would short-circuit it
        // anyway, but skipping early avoids the redundant phase churn
        // and the noisy `session.bootstrap.dedupe` log line.
        if (
          sessionStatus.kind === 'authenticated' &&
          phase.kind === 'authenticated'
        ) {
          log.info({ event: 'app.onAuthenticated.alreadyAuthenticated' });
          return;
        }
        // Recovery backup is EJECTED from signup. A fresh signup now
        // lands straight in the app; the recovery-key setup is
        // introduced afterwards by the first-run popover guide
        // (`RecoveryGuideTour`) and stays available in Settings. We no
        // longer divert into a forced `creatingRecoveryBackup` gate, so
        // never latch the prompt here. (`recoveryBackupRequired` is
        // intentionally unused now — the backend `RequireBackedUp` gate
        // is treated as non-fatal.)
        //
        // Instead, latch the authenticated-phase effect to ensure a
        // revealable recovery backup exists once the SDK is live
        // (fire-and-forget; never gates AppShell). This fires for ANY OTP
        // auth reaching here — signup AND sign-in — because a sign-out wipes
        // the local mnemonic and a sign-in re-onboards a fresh identity that
        // makes the old server blob stale; the backup task is idempotent so a
        // re-verify that kept the phrase never rotates the key. Link / recovery
        // use separate hosts and never reach `onAuthenticated`.
        void wasFreshSignup;
        ensureRecoveryBackupRef.current = true;
        void recoveryBackupRequired;
        pendingRecoveryBackupRef.current = false;
        recoveryBackupMandatoryRef.current = false;
        setPhase({ kind: 'bootstrapping' });
        withTimeout(
          bootstrapWebSession({
            tokens: user.tokens,
            identity: user.identity,
          }),
          BOOTSTRAP_DEADLINE_MS,
          'onAuthenticated.bootstrap',
        ).catch(async (error: unknown) => {
          if (error instanceof BootstrapFailure) {
            log.warn(
              { event: 'app.bootstrap.failed', reason: error.reason },
              'session bootstrap rejected',
            );
          } else {
            log.error({ event: 'app.bootstrap.failed' }, 'session bootstrap rejected');
          }
          // Bootstrap failed → wipe every layer of local state so the
          // next reload doesn't loop the user through the same failed
          // silent re-auth. Tokens + identity alone leaves crumbs
          // (MLS storage, Dexie chat tables, cursors) that resurface
          // as "ghost 401" / "ghost 404" noise on next mount.
          await goUnauthenticated('bootstrap.failed');
        });
      }}
    />
  );
}

const styles = StyleSheet.create({
  fallback: {
    flex: 1,
    padding: 24,
  } satisfies ViewStyle,
});
